import 'braid-design-system/reset';
import 'core-js/es/map';
import 'core-js/es/set';
import 'promise-polyfill/src/polyfill';

import { ApolloProvider } from '@apollo/client';
import { HubbleProvider, Hubble } from '@seek/hubble';
import { MelwaysProvider } from '@seek/melways-react';
import { setup, metrics } from '@seek/metrics-js';
import { AuthN, getConfig } from '@seek/online-identity';
import { VocabProvider } from '@vocab/react';
import Bowser from 'bowser';
import { BraidProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { AppConfigProvider, type Config } from 'src/config/context';
import App from 'src/containers/App';
import { initBrowserLog } from 'src/modules/error-logger/browserLog';
import { PreferencesProvider } from 'src/preferences';

import { createClient } from './graphql/client';

export default async (config: Config) => {
  const {
    navigator = { userAgent: 'render-default' },
    location = { host: '' },
  } = typeof window !== 'undefined' ? window : {};
  const bowserDevice = Bowser.parse(navigator.userAgent);
  const host = location.host;
  let loginId: string | undefined;
  let isTestUser = false;

  if (Boolean(host)) {
    try {
      const auth = new AuthN(getConfig('seekAnz-candidate', { host }));

      await auth.init();

      const result = await auth.getTestUserContext();

      isTestUser = result ? result.testRecord : false;
      loginId = (await auth.getUser())['http://seek/claims/user_id'];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      isTestUser = false;
    }
  }

  const {
    platform: { type: deviceType = 'desktop' },
    browser: { name: browserName },
  } = bowserDevice;

  const globalTags = [
    `team:apply-experience`,
    `environment:${config.environment}`,
    `browser-name:${browserName}`,
    `device-type:${deviceType}`,
    `app-version:${config.VERSION}`,
    `seek-test:${isTestUser}`,
  ];

  initBrowserLog(globalTags);

  if (isTestUser) {
    window.SEEK_TEST = true;
  }

  setup({
    host: config.METRICS_URL,
    isEnabled: config.METRICS_ENABLED,
    prefix: 'ca_myjobs_ui',
    globalTags,
  });
  metrics.sendPagePerformance();

  const apolloClient = createClient({
    webVersion: config.VERSION,
    mockedAuthEnabled: true,
    graphqlEndpointOverride: config.MOCKED_GRAPHQL_ENABLED
      ? config.MOCKED_GRAPHQL_ENDPOINT
      : undefined,
    language: config.LANGUAGE,
    country: config.COUNTRY,
    brand: config.BRAND,
  });

  const hubble = new Hubble({
    appName: 'ca-myjobs',
    appVersion: config.VERSION,
    brand: config.BRAND.toLowerCase(),
    country: config.COUNTRY.toUpperCase(),
    isProduction: config.environment === 'production',
    loginId,
    pageName: 'my activity',
    ...(isTestUser
      ? { tags: { testRecord: 'true', testScope: 'load-test-apply' } }
      : {}),
    experience: 'candidate',
  });

  const rootContainer = document.getElementById('app');
  const root = createRoot(rootContainer!);

  root.render(
    <MelwaysProvider.Static
      language={config.LANGUAGE}
      path={config.ROUTE}
      site={config.SITE}
    >
      <AppConfigProvider config={config}>
        <ApolloProvider client={apolloClient}>
          <VocabProvider language={config.LANGUAGE}>
            <BrowserRouter>
              <HubbleProvider hubble={hubble}>
                <BraidProvider theme={seekJobs}>
                  <PreferencesProvider>
                    <App />
                  </PreferencesProvider>
                </BraidProvider>
              </HubbleProvider>
            </BrowserRouter>
          </VocabProvider>
        </ApolloProvider>
      </AppConfigProvider>
    </MelwaysProvider.Static>,
  );
};
